import type React from "react";
import { type DragEvent, useState, useCallback } from "react"; // Removed useRef unless needed


import { useDispatch, useSelector } from "react-redux";
import { useTranslate } from "@tolgee/react";
import { Spinner } from "react-bootstrap";

import {
    handleFileUpload,
    resetUploadState, 
    setCancelled,
    startLoading,
    stopLoading,
} from "../../../../../redux/slices/bulkPostsImport"; // Adjust path
import { lomavisShowToastr } from "../../../../../redux/slices/utils/ui/toastr"; // Adjust path

// UI Components
import ModalBody from "../../../../modals/modalBody"; // Adjust path
import { LomavisTitle1 } from "../../../../generic/title/lomavisTitle1"; // Adjust path
import { ResultsTableBody } from "./resultsTable"; // Adjust path
import CsvDownload from "./downloadExampleCSV";

// --- Constants ---
const ALLOWED_FILE_TYPES = ["text/csv"];
const CSV_FILE_EXTENSION = ".csv";

// --- Main Component ---
export const BulkPostsImportModal = ({ handleClose }: { handleClose: () => void }) => {
    const dispatch = useDispatch();
    const { t } = useTranslate();

    const {
        uploadingStarted,
        uploadDone,
        totalPosts,
        uploadedPostsCount,
        errorsList,
        rowStatuses,
        cancelled,
        controller,
        ui:{
          showImportModal
        }
    } = useSelector((state: any) => state.bulkPostsImport);

    const { locations } = useSelector((state: any) => state.locationsList);

    const [dragIsOver, setDragIsOver] = useState(false);

    // --- Event Handlers ---
    const handleDragOver = useCallback((event: DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        setDragIsOver(true);
    }, []); // setDragIsOver is stable

    const handleDragLeave = useCallback((event: DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        setDragIsOver(false);
    }, []);

    const showInvalidFileToast = useCallback(() => {
         lomavisShowToastr(t("lomavisCreator.bulkPostsImportModal.notAValidCSVFile"), "error")
    }, [dispatch, t]); 

    const processFile = useCallback((file: File | null | undefined) => {
        if (!file) return;
        const isAllowedType = ALLOWED_FILE_TYPES.includes(file.type);
        const hasCsvExtension = file.name.toLowerCase().endsWith(CSV_FILE_EXTENSION);
        if (!isAllowedType && !hasCsvExtension) {
            showInvalidFileToast();
            return;
        }
        dispatch(startLoading());
        dispatch(handleFileUpload(
            file,
            locations,
            () => {  },
            (error) => {
                console.error("File upload failed:", error);
                dispatch(stopLoading()); // Ensure loading stops on error
            },
        ) as any);
    }, [dispatch, locations, showInvalidFileToast]); // Dependencies

    const handleDrop = useCallback((event: DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        setDragIsOver(false); // Directly use stable setter
        const file = event.dataTransfer.files?.[0];
        processFile(file);
    }, [processFile]); // Dependency

    const onFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        processFile(file);
        e.target.value = ''; // Reset input
    }, [processFile]); // Dependency

    const handleResetAndUpload = useCallback(() => {
        // Called by the "Upload Another File" button in error state
        dispatch(stopLoading()); // Ensure loading is off
        dispatch(resetUploadState()); // Reset for new upload
    }, [dispatch]);

    // --- Render Functions for Content Area ---
    // These functions are defined within the component scope and have access to its state/props/handlers

    const renderInitialState = () => (
      <div
      className="d-flex flex-column justify-content-between h-100 p-4"
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      style={{
        backgroundColor: dragIsOver ? "rgba(0, 0, 0, 0.05)" : "transparent",
        border: "2px dashed",
        borderColor: dragIsOver ? "#0d6efd" : "#ced4da",
        borderRadius: "0.5rem",
      }}
  >
{/* Top Section: White box with help text and download */}
<div className="bg-white rounded shadow-sm p-3 mb-4 d-flex justify-content-center flex-column">
                    <p className="text-muted mb-2">
                      {t("lomavisCreator.bulkPostsImportModal.helpText")}
                    </p>
                    <div className={"d-flex justify-content-between"}>
                        <div>
                            <a className={"btn btn-sm btn-secondary"} href={t("lomavisCreator.bulkPostsImportModal.bulkCreateHelpbaseLinkLink")} target="_blank" rel="app.lomavis.com"><span><i className={"fa-duotone fa-circle-info"}></i></span> <span>{t("lomavisCreator.bulkPostsImportModal.bulkCreateHelpbaseLinkTitle")}</span></a>
                        </div>
                      <CsvDownload />
                    </div>
                  </div>

                  {/* Centered Upload Area */}
                  <div className="d-flex flex-column justify-content-center align-items-center text-center flex-grow-1">
            <label htmlFor="file-upload" className="cursor-pointer text-center">
                <i className="fa-duotone fa-solid fa-cloud-arrow-up fs-5x"></i>
                <div className="text-center mt-3">
                    <p className="fw-bold">{t("lomavisCreator.bulkPostsImportModal.dropCsvFile")}</p>
                    <p className="text-muted">{t("lomavisCreator.bulkPostsImportModal.clickToSelectFile")}</p>
                </div>
            </label>
            <input
                id="file-upload"
                type="file"
                className="d-none"
                onChange={onFileChange}
                accept={CSV_FILE_EXTENSION + ", " + ALLOWED_FILE_TYPES.join(',')}
            />
        </div>
        </div>
    );

    const renderUploadingState = () => (
        <div className="d-flex h-100 flex-column align-items-center justify-content-center p-4 text-center">
            {!uploadDone && (
                <div className="mb-3">
                    <Spinner animation="border" variant="primary" />
                </div>
            )}
            <p className="fw-bold fs-5 mb-2">
                {uploadedPostsCount} / {totalPosts} {t("lomavisCreator.bulkPostsImportModal.uploading")}
            </p>

                <div>
                    {t("lomavisCreator.bulkPostsImportModal.uploadingHelptext")}
                </div>
        
        </div>
    );

  const renderResultsState = (errorsList: any[]) => (
      <div className="h-100 p-4">
          <p className="fw-bold fs-5 mb-2">
            {t("lomavisCreator.bulkPostsImportModal.results")}
          </p>
          {errorsList.length > 0 && (
              <div className="text-danger">
                  {t("lomavisCreator.bulkPostsImportModal.someErrorsOccurred")}
              </div>
          )}
          <ResultsTableBody rowStatuses={rowStatuses} />

      </div>
  );

    const renderErrorState = () => (
        <div className="h-100 d-flex flex-column p-4">
            <div className="alert alert-danger mb-3">
                <h5 className="alert-heading mb-0">{t("lomavisCreator.bulkPostsImportModal.errors")}</h5>
            </div>
            <div className="d-flex justify-content-end mb-3">
                <button
                    className="btn btn-primary btn-sm"
                    onClick={handleResetAndUpload} // Reset state for new upload
                >
                    {t("lomavisCreator.bulkPostsImportModal.uploadAnotherFile")}
                </button>
            </div>
            <div className="flex-grow-1 overflow-auto">
                <table className="table table-striped table-hover mb-0">
                    <thead>
                        <tr>
                        <th className={"fw-bold"} scope="col">{t("lomavisCreator.bulkPostsImportModal.row")}</th>
                        <th className={"fw-bold"} scope="col">{t("lomavisCreator.bulkPostsImportModal.error")}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {errorsList.map((error, index) => (
                            <tr key={`${error.rowIndex}-${index}`}> {/* Slightly better key */}
                                <td className="align-middle">{error.rowIndex + 1}</td>
                                <td>
                                    <div>{t(`lomavisCreator.bulkPostsImportModal.${error.error}`, error.error)}</div>
                                    {error.platform && (
                              <div className="text-dark">
                                {t("lomavisCreator.bulkPostsImportModal.platform")}: {error.platform}
                              </div>
                          )}
                        
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );

    // --- Render ---
    if (!showImportModal) return null; // Early return if modal is not shown
    return (
        <ModalBody onClose={handleClose} show={showImportModal}>
            {/* Overall Structure */}
            <div className="bg-secondary d-flex flex-column gap-3 justify-content-between" style={{ height: "100%" }}>
                {/* Header Section */}
                <div className="card w-100 rounded p-2 d-flex shadow-sm h-60px d-flex flex-column justify-content-center">
                    <div className="d-flex flex-row justify-content-start">
                        <LomavisTitle1 text={t("lomavisCreator.bulkPostsImport.title")} />
                    </div>
                </div>

                {/* Content Section - Conditionally render internal state */}
                <div className="d-flex flex-column gap-3 card h-100 flex-grow-1">
                    {errorsList.length > 0 && !uploadDone && renderErrorState()}
                    {uploadingStarted && !uploadDone && errorsList.length === 0 && renderUploadingState()}
                      {!uploadingStarted && !uploadDone && errorsList.length === 0 && renderInitialState()}
                      {uploadDone && uploadingStarted && renderResultsState(errorsList)}
                </div>

                {/* Footer Section */}
                <div className="card w-100 rounded p-2 shadow-sm">
                    <BulkPostsImportButtonsBar
                        onClose={handleClose}
                        uploadingStarted={uploadingStarted}
                        cancelled={cancelled}
                        controller={controller}
                        uploadDone={uploadDone} 
                    />
                </div>
            </div>
        </ModalBody>
    );
};


// --- Button Bar Component ---
interface BulkPostsImportButtonsBarProps {
    onClose: () => void;
    uploadingStarted: boolean;
    cancelled: boolean;
    controller: AbortController | null;
    uploadDone: boolean;
}

export const BulkPostsImportButtonsBar = ({
    onClose,
    uploadingStarted,
    cancelled,
    controller,
    uploadDone,
}: BulkPostsImportButtonsBarProps) => {
    const { t } = useTranslate();
    const dispatch = useDispatch();

    const handleCancel = () => {
        if (controller) {
            controller.abort();
            dispatch(setCancelled(true));
        }
    }
    const handleCloseClick = () => {
        dispatch(stopLoading()); // Ensure spinner stops if modal is closed
        onClose(); // Call the handler passed from the parent
    }

    return (
        <div className="d-flex justify-content-end align-items-center gap-2">
            {/* Show Cancel button only when actively uploading and not already cancelled */}
            {uploadingStarted  && controller && !uploadDone &&  (
                 <button
                    className="btn btn-danger btn-sm"
                    onClick={handleCancel}
                    disabled={cancelled}
                >
                   { controller.signal.aborted && !cancelled && <Spinner animation="border" size="sm" className="me-2" />}
                  {controller.signal.aborted  && !cancelled ? t("lomavisCreator.bulkPostsImportModal.cancelling") : t("lomavisCreator.bulkPostsImportModal.cancel")}
                </button>
            )}
            {(!uploadingStarted || uploadDone) && (
                <button
                    className="btn btn-secondary btn-sm"
                    onClick={handleCloseClick}
                >
                    {t("close")}
                </button>
            )}
        </div>
    );
};
